/**
 * SCRUM-158 (P-8) — Zod schemas for the SeasonGroup + Section + TimelineWindow +
 * Assignment HTTP boundary.
 *
 * Endpoints covered (spec §10.1):
 *   #1  POST   /organizations/:slug/season-groups                              CreateSeasonGroup
 *   #2  PATCH  /organizations/:slug/season-groups/:id                          UpdateSeasonGroupMetadata
 *   #3  DELETE /organizations/:slug/season-groups/:id                          DeleteSeasonGroup (path-param only)
 *   #4  POST   /organizations/:slug/season-groups/:id/activate                 ActivateSeasonGroup
 *   #5  POST   /organizations/:slug/season-groups/:id/deactivate               DeactivateSeasonGroup
 *   #6  GET    /organizations/:slug/season-groups/:id/validate-activation      ValidateSeasonGroupForActivation
 *   #7  POST   /organizations/:slug/season-groups/:id/sections                 AddSection
 *   #8  PATCH  /organizations/:slug/season-groups/:id/sections/:sectionId      UpdateSection
 *   #9  POST   .../sections/:sectionId/archive                                 ArchiveSection
 *   #10 POST   .../timeline-windows                                            AssignTimelineWindow
 *   #11 DELETE .../timeline-windows/:windowId                                  RemoveTimelineWindow
 *   #12 POST   /organizations/:slug/season-groups/:id/assignments              AssignSeasonGroupToProperties
 *   #13 POST   .../season-group-assignments/:assignmentId/unassign             UnassignSeasonGroupFromProperty
 *   #14 GET    /organizations/:slug/season-groups/:id/assignments              ListAssignmentsForSeasonGroup
 *   #15 GET    /organizations/:slug/season-groups                              ListSeasonGroups
 *   #16 GET    /organizations/:slug/season-groups/:id                          GetSeasonGroup
 *
 * ModifierSchema is defined here and re-exported for use by price-adjustment.ts
 * and floor-rate-override.ts (both consume Modifier display-unit shapes).
 *
 * Adversarial tests: packages/validators/src/__tests__/pricing/season-group.spec.ts
 */
import { z } from 'zod';
import { isoLocalDateString, instantString } from './_shared.js';
// ---------------------------------------------------------------------------
// ModifierSchema — discriminated tagged union (display units)
// Percent stored as DB BIGINT(percent * 100); the mapper handles the round-trip.
// Display values: `{ kind: 'percent', value: 25.5 }` or
//                 `{ kind: 'flat-offset', valueCents: 5000 }`.
// ---------------------------------------------------------------------------
/**
 * Modifier display-unit schema used in Section create/update requests.
 * Re-exported for consumption by price-adjustment.ts (ApplyPriceAdjustmentRequestSchema).
 *
 * Percent bounds: [-100, 1000] with at-most-two-decimal precision (`.multipleOf(0.01)`).
 * Flat-offset: integer Cents (no nonnegative constraint — discounts are negative).
 */
export const ModifierSchema = z.discriminatedUnion('kind', [
    z.object({
        kind: z.literal('percent'),
        value: z.number().min(-100).max(1000).multipleOf(0.01),
    }),
    z.object({
        kind: z.literal('flat-offset'),
        valueCents: z.number().int(),
    }),
]);
// ---------------------------------------------------------------------------
// WeekdayWeekendMappingSchema
// Shape mirrors WeekdayWeekendMappingInput in weekday-weekend-mapping.vo.ts:
//   { weekdayDays: DayOfWeek[]; weekendDays: DayOfWeek[] }
// Both arrays are non-empty sub-sets of the 7-value DayOfWeek union.
// Domain invariants (exhaustive partition / mutual exclusion) are enforced
// downstream by WeekdayWeekendMapping.create(); the schema validates shape only.
// ---------------------------------------------------------------------------
const DayOfWeekSchema = z.enum(['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN']);
export const WeekdayWeekendMappingSchema = z.object({
    weekdayDays: z.array(DayOfWeekSchema).min(1).max(6),
    weekendDays: z.array(DayOfWeekSchema).min(1).max(6),
});
// ---------------------------------------------------------------------------
// CreateSeasonGroupRequestSchema — endpoint #1
// ---------------------------------------------------------------------------
/**
 * POST /organizations/:slug/season-groups body.
 * `type` defaults to 'manual' when omitted.
 */
export const CreateSeasonGroupRequestSchema = z.object({
    name: z.string().min(1).max(80),
    type: z.enum(['manual', 'integrated']).optional(),
    mapping: WeekdayWeekendMappingSchema.optional(),
});
// ---------------------------------------------------------------------------
// UpdateSeasonGroupMetadataRequestSchema — endpoint #2
// ---------------------------------------------------------------------------
const UpdateSeasonGroupMetadataFieldsShape = z.object({
    name: z.string().min(1).max(80).optional(),
    mapping: WeekdayWeekendMappingSchema.optional(),
});
/**
 * PATCH /organizations/:slug/season-groups/:id body.
 * At least one mutable field must be provided.
 */
export const UpdateSeasonGroupMetadataRequestSchema = UpdateSeasonGroupMetadataFieldsShape.refine((req) => Object.values(req).some((v) => v !== undefined), { message: 'at least one field (name or mapping) must be provided' });
// ---------------------------------------------------------------------------
// AddSectionRequestSchema — endpoint #7
// ---------------------------------------------------------------------------
/**
 * POST /organizations/:slug/season-groups/:id/sections body.
 */
export const AddSectionRequestSchema = z.object({
    name: z.string().min(1).max(60),
    weekdayModifier: ModifierSchema,
    weekendModifier: ModifierSchema,
    minStayNights: z.number().int().min(1).max(30),
});
// ---------------------------------------------------------------------------
// UpdateSectionRequestSchema — endpoint #8
// ---------------------------------------------------------------------------
const UpdateSectionFieldsShape = z.object({
    name: z.string().min(1).max(60).optional(),
    weekdayModifier: ModifierSchema.optional(),
    weekendModifier: ModifierSchema.optional(),
    minStayNights: z.number().int().min(1).max(30).optional(),
});
/**
 * PATCH /organizations/:slug/season-groups/:id/sections/:sectionId body.
 * At least one field must be provided.
 */
export const UpdateSectionRequestSchema = UpdateSectionFieldsShape.refine((req) => Object.values(req).some((v) => v !== undefined), { message: 'at least one section field must be provided' });
// ---------------------------------------------------------------------------
// AssignTimelineWindowRequestSchema — endpoint #10
// ---------------------------------------------------------------------------
/**
 * POST /organizations/:slug/season-groups/:id/timeline-windows body.
 * Semi-open range: dateFrom < dateTo (dateTo is exclusive).
 */
export const AssignTimelineWindowRequestSchema = z
    .object({
    sectionId: z.string().uuid(),
    dateFrom: isoLocalDateString,
    dateTo: isoLocalDateString,
})
    .refine((req) => req.dateFrom < req.dateTo, {
    message: 'dateFrom must be before dateTo (semi-open range)',
    path: ['dateTo'],
});
// ---------------------------------------------------------------------------
// AssignSeasonGroupToPropertiesRequestSchema — endpoint #12
// ---------------------------------------------------------------------------
/**
 * POST /organizations/:slug/season-groups/:id/assignments body.
 * Max 500 property IDs per batch (mirrors BulkSetBaselineRequestSchema precedent).
 */
export const AssignSeasonGroupToPropertiesRequestSchema = z.object({
    propertyIds: z.array(z.string().uuid()).min(1).max(500),
});
// ---------------------------------------------------------------------------
// Response schemas — used by OpenAPI annotations only
// ---------------------------------------------------------------------------
/**
 * SectionResponseSchema — sub-resource shape inside SeasonGroupResponseSchema.
 */
export const SectionResponseSchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    weekdayModifier: ModifierSchema,
    weekendModifier: ModifierSchema,
    minStayNights: z.number().int().min(1),
    archivedAt: instantString.nullable(),
});
/**
 * TimelineWindowResponseSchema — sub-resource shape inside SeasonGroupResponseSchema.
 */
export const TimelineWindowResponseSchema = z.object({
    id: z.string().uuid(),
    sectionId: z.string().uuid(),
    dateFrom: isoLocalDateString,
    dateTo: isoLocalDateString,
});
/**
 * SeasonGroupResponseSchema — GET /season-groups/:id response (endpoint #16).
 */
export const SeasonGroupResponseSchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    type: z.enum(['manual', 'integrated']),
    status: z.enum(['INCOMPLETE', 'ACTIVE', 'WARNING', 'EXPIRED']),
    mapping: WeekdayWeekendMappingSchema.nullable(),
    sections: z.array(SectionResponseSchema),
    timelineWindows: z.array(TimelineWindowResponseSchema),
    createdAt: instantString,
    updatedAt: instantString,
});
/**
 * AssignmentResponseSchema — entry in the assignments list (endpoint #14).
 */
export const AssignmentResponseSchema = z.object({
    assignmentId: z.string().uuid(),
    propertyId: z.string().uuid(),
    seasonGroupId: z.string().uuid(),
    effectiveFrom: isoLocalDateString,
    unassignedAt: isoLocalDateString.nullable(),
});
// ---------------------------------------------------------------------------
// ValidateActivationResponseSchema — endpoint #6
// ActivationFailureReason is a rich discriminated union in the domain layer;
// the HTTP boundary projects it to a simple JSON shape.
// ---------------------------------------------------------------------------
/**
 * HTTP projection of ActivationFailureReason (spec §10.2).
 * The domain VO carries typed detail fields (gapStart, windowA, etc.);
 * the wire shape serialises them as a flat object with `kind` discriminant
 * plus optional detail keys.
 */
export const ActivationFailureReasonDtoSchema = z
    .object({
    kind: z.enum([
        'no-sections',
        'no-windows',
        'coverage-insufficient',
        'no-gap-violated',
        'no-overlap-violated',
        'inactive-section-referenced',
    ]),
    // Optional detail fields — present only for the matching kind.
    coverageMonthsRemaining: z.number().int().nonnegative().optional(),
    requiredMonths: z.literal(24).optional(),
    gapStart: isoLocalDateString.optional(),
    gapEnd: isoLocalDateString.optional(),
    windowA: z.string().uuid().optional(),
    windowB: z.string().uuid().optional(),
    overlapStart: isoLocalDateString.optional(),
    overlapEnd: isoLocalDateString.optional(),
    sectionId: z.string().uuid().optional(),
    windowId: z.string().uuid().optional(),
});
/**
 * ValidateActivationResponseSchema — GET .../validate-activation response (endpoint #6).
 */
export const ValidateActivationResponseSchema = z.object({
    canActivate: z.boolean(),
    reasons: z.array(ActivationFailureReasonDtoSchema),
    coverageMonthsRemaining: z.number().int().nonnegative(),
    coverageStart: isoLocalDateString.nullable(),
    coverageEnd: isoLocalDateString.nullable(),
});
/**
 * SeasonGroupListResponseSchema — GET /season-groups response (endpoint #15).
 */
export const SeasonGroupListResponseSchema = z.object({
    items: z.array(SeasonGroupResponseSchema),
});
/**
 * AssignmentListResponseSchema — GET .../assignments response (endpoint #14).
 */
export const AssignmentListResponseSchema = z.object({
    items: z.array(AssignmentResponseSchema),
});
//# sourceMappingURL=season-group.js.map